import React from 'react';
import { Box, IconButton, InputBase, styled } from '@mui/material';
import { Search } from '@mui/icons-material';
import { ReactComponent as SearchClearButtonSvg } from 'assets/icons/SearchClearButton.svg';
import { useSearchBar } from 'features/resources/hooks/useSearchBar';

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  borderRadius: '40px',
  border: `1px solid ${theme.palette.grey[400]}`,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(0.5),
  height: 49,
  width: '100%',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  color: theme.palette.grey[700],
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(0.75),
  '& input': {
    padding: 0,
    color: theme.palette.grey[800],
    '&::placeholder': {
      color: theme.palette.grey[600],
      opacity: 1,
    },
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

export interface SearchBarProps {
  /** Current search term */
  value: string;
  /** Called on every keystroke */
  onChange: (value: string) => void;
  /** Called on Enter or the mobile keyboard Search key */
  onSubmit?: (value: string) => void;
  /** Called when the search bar container is clicked */
  onClick?: () => void;
  /** Called when × is tapped; clears value if omitted */
  onClear?: () => void;
  /** Overrides the default placeholder */
  placeholder?: string;
  /** Focuses the input on mount */
  autoFocus?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSubmit,
  onClick,
  onClear,
  placeholder,
  autoFocus,
}) => {
  const { resolvedPlaceholder, handleKeyDown, handleClear } = useSearchBar({
    value,
    onChange,
    onSubmit,
    onClear,
    placeholder,
  });

  return (
    <SearchContainer className="search-bar" onClick={onClick}>
      <IconContainer aria-hidden="true">
        <Search />
      </IconContainer>

      <StyledInputBase
        className="search-bar__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={resolvedPlaceholder}
        inputProps={{
          enterKeyHint: 'search',
          'aria-label': resolvedPlaceholder,
          autoFocus,
        }}
      />

      {value.length > 0 && (
        <ClearButton
          className="search-bar__clear-button"
          onClick={handleClear}
          aria-label="Clear search"
          size="small"
        >
          <SearchClearButtonSvg />
        </ClearButton>
      )}
    </SearchContainer>
  );
};
